import React from 'react';
import { motion } from 'framer-motion';
import { FaHeart, FaShoppingCart, FaTrash, FaEye } from 'react-icons/fa';
import { FavoriteOrder } from '../../services/favoriteService';
import { formatOrderDateTime, formatPrice } from '../../utils/orderUtils';

interface FavoriteOrderCardProps {
  favorite: FavoriteOrder;
  index: number;
  onRemove: (orderId: number) => void;
  onReorder: (favorite: FavoriteOrder) => void;
  onViewDetails: (favorite: FavoriteOrder) => void;
  onToggleFavorite?: (orderId: number) => void;
}

const FavoriteOrderCard: React.FC<FavoriteOrderCardProps> = ({
  favorite,
  index,
  onRemove,
  onReorder,
  onViewDetails,
  onToggleFavorite,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.1, 0.5) }}
      className="bg-gradient-to-r from-pink-50 to-rose-50 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-pink-100 hover:shadow-xl transition-all duration-200"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onToggleFavorite?.(favorite.order_id)}
            className="p-3 bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl text-white hover:from-pink-600 hover:to-rose-600 transition-all transform hover:scale-105"
            title="Favorilerden kaldır"
          >
            <FaHeart />
          </button>
          <div>
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              Sipariş #{favorite.order_id}
              <span className="text-xs bg-pink-100 text-pink-600 px-2 py-1 rounded-full font-medium">
                Favori
              </span>
            </h3>
            <p className="text-gray-600 font-medium">
              {formatOrderDateTime(favorite.order_created_at)}
            </p>
            <p className="text-xs text-gray-500">
              Favoriye eklendi: {formatOrderDateTime(favorite.favorited_at)}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-800 mb-2">
            {formatPrice(favorite.total_amount || 0)}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span>{favorite.item_count} ürün</span>
          </div>
        </div>
      </div>

      {/* Sipariş Ürünleri */}
      {favorite.items && favorite.items.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Sipariş Ürünleri:</h4>
          <div className="space-y-1">
            {favorite.items.slice(0, 3).map((item, itemIndex) => (
              <div key={itemIndex} className="text-sm text-gray-600 flex justify-between">
                <span>
                  {item.name}
                  {item.size && item.size !== 'normal' && (
                    <span className="text-xs text-gray-500">
                      {' '}
                      ({item.size === 'double' ? 'Double' : item.size})
                    </span>
                  )}
                  {item.strength && item.strength !== 'normal' && (
                    <span className="text-xs text-gray-500">
                      {' '}
                      -{' '}
                      {item.strength === 'light'
                        ? 'Açık'
                        : item.strength === 'strong'
                          ? 'Demli'
                          : item.strength}
                    </span>
                  )}
                </span>
                <span className="font-medium">
                  {item.quantity}x {formatPrice(item.price)}
                </span>
              </div>
            ))}
            {favorite.items.length > 3 && (
              <div className="text-xs text-gray-500 italic">
                +{favorite.items.length - 3} ürün daha...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Aksiyon Butonları */}
      <div className="flex items-center gap-2 pt-4 border-t border-pink-100">
        <button
          onClick={() => onViewDetails(favorite)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white text-gray-600 rounded-lg font-medium border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          <FaEye />
          Detay
        </button>

        <button
          onClick={() => onReorder(favorite)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-400 text-white rounded-lg font-medium shadow-md hover:from-green-600 hover:to-green-500 transition-all"
        >
          <FaShoppingCart />
          Sipariş Ver
        </button>

        <button
          onClick={() => onRemove(favorite.order_id)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-400 text-white rounded-lg font-medium shadow-md hover:from-red-600 hover:to-red-500 transition-all"
        >
          <FaTrash />
          Sil
        </button>
      </div>
    </motion.div>
  );
};

export default FavoriteOrderCard;
